/**
 * Platform console → Marketing: campaigns of prospecting email, drafted from
 * the library templates, fed by a CSV, and drained by the outbox worker
 * under the platform-wide send throttle.
 *
 * Two views in this file: the campaign list (/platform/marketing) and one
 * campaign (/platform/marketing/:id), where the list is uploaded and the
 * sending is started. The console never sends to the list itself — Start
 * flips a status, and the rate card explains the pace honestly.
 *
 * R12 (screen 44): the design's page head with "+ New campaign", the tiles
 * (Last 24 hours · This hour — rolling windows across the platform, shared
 * with invitations and reminders — · Best open rate · Unsubscribed), the pace
 * sentence, and the Campaigns table card. Its "List" column is the template,
 * since a list is uploaded per campaign. Deleting a campaign asks once, on
 * both views — it takes the list with it.
 *
 * Tracking (072, Ben 2026-09-26): an open is the email's pixel loading, a
 * bounce is a notice read from the sending mailbox and matched to the email
 * by its Message-ID. Rates are over what was DELIVERED — sent less hard
 * bounces — since an address that never got the email cannot open it.
 */
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CampaignListRow, CampaignStatus, MarketingCampaign, MarketingRecipient, MarketingTemplate, SendWindow, Suppression,
  createCampaignApi, deleteCampaignApi, getCampaignApi, listCampaignsApi, listSuppressionsApi, pauseCampaignApi,
  previewCampaignApi, removeCampaignRecipientApi, removeSuppressionApi, startCampaignApi, testCampaignApi,
  updateCampaignApi, uploadCampaignCsvApi,
} from '../../api/marketing';
import { FigureTile, Modal, RowMenu, TableCard } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { usePlatform } from './PlatformLayout';

const STATUS_PILL: Record<CampaignStatus, string> = {
  draft: 'neutral', sending: 'ok', paused: 'caution', done: 'identity',
};

export function StatusPill({ status }: { status: CampaignStatus }) {
  return <span className={`fs-pill ${STATUS_PILL[status]}`}>{status}</span>;
}

const when = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/** A "+ New …" label whose accessible name is the words alone. */
const Plus = ({ children }: { children: string }) => <><span aria-hidden="true">+</span> {children}</>;

/** Sent less hard bounces: who could have opened it. */
const deliveredOf = (sent: number, bounced: number) => sent - bounced;
/** "44%" — opens are estimates, so a decimal would claim more than they know. */
const wholePct = (n: number, of: number) => `${Math.round((n / of) * 100)}%`;
/** "1.2%" — a bounce rate lives in the low single digits, so it keeps one. */
const tenthPct = (n: number, of: number) => `${Math.round((n / of) * 1000) / 10}%`;
/** Amber from 2% and red from 5%, as Services › Email's Health row reads the platform's bounce rate. */
const bounceTone = (n: number, of: number): 'warn' | 'danger' | undefined =>
  (of > 0 && n / of >= 0.05 ? 'danger' : of > 0 && n / of >= 0.02 ? 'warn' : undefined);

/**
 * Below 20 delivered one open moves the rate by five points or more, so a
 * tiny test campaign would always "win" the Best open rate tile.
 */
const BEST_OPEN_MIN_DELIVERED = 20;

function bestOpenRate(campaigns: CampaignListRow[]) {
  let best: { campaign: CampaignListRow; opened: number; delivered: number } | null = null;
  for (const c of campaigns) {
    if (c.opened === undefined || c.opened <= 0) continue;
    const delivered = deliveredOf(c.sent, c.bounced ?? 0);
    if (delivered < BEST_OPEN_MIN_DELIVERED) continue;
    if (!best || c.opened / delivered > best.opened / best.delivered) best = { campaign: c, opened: c.opened, delivered };
  }
  return best;
}

/** The Opened cell: "44%", or "—" before anything is delivered (or from a list read without tracking). */
function openedCell(c: CampaignListRow) {
  const delivered = deliveredOf(c.sent, c.bounced ?? 0);
  return c.opened === undefined || delivered <= 0 ? '—' : wholePct(c.opened, delivered);
}

/** The Bounced cell: "3 · 1.2%" of what was sent, and the temporary refusals under it. */
function BouncedCell({ c }: { c: CampaignListRow }) {
  if (c.bounced === undefined || c.sent === 0) return <>—</>;
  return (
    <>
      {c.bounced === 0 ? '0' : `${c.bounced} · ${tenthPct(c.bounced, c.sent)}`}
      {(c.soft_bounced ?? 0) > 0 && <div className="fs-cell-sub faint">{c.soft_bounced} temporary</div>}
    </>
  );
}

/**
 * Every marketing route answers 403 below Super Admin (`marketing.send`), so
 * a staff member or an Admin is told that plainly rather than shown the raw
 * refusal over an empty table.
 */
const NEEDS_SUPERADMIN = 'Marketing email needs the Super Admin platform role.';
const refused = (e: unknown) => (e as { status?: number })?.status === 403;

/**
 * The pace, told before anybody asks why 500 emails are not out yet. Shared
 * by both marketing views.
 */
export function SendWindowCard({ window: w, kind }: { window: SendWindow; kind: string }) {
  return (
    <p className="pl-note pm-pace">
      Sending pace: <b>{w.lastHour}</b> of {w.hourlyCap} this hour · <b>{w.lastDay}</b> of {w.dailyCap} in
      the last 24 hours, across the whole platform. {kind} drain automatically at this pace — it keeps the
      sending address inside Gmail’s bulk-sender limits instead of getting it marked as spam.
    </p>
  );
}

/** Where outgoing email is set up — one click from the warning. */
const EmailSettingsLink = () => <Link to="/platform/settings?tab=email">Services → Email</Link>;

/**
 * The one confirm a campaign delete asks (R12): it is irreversible, and the
 * uploaded list goes with it. `onConfirm` throws to keep the dialog open with
 * the server's sentence in it.
 */
function DeleteCampaignModal({ campaign, recipients, onConfirm, onClose }: {
  campaign: { name: string };
  /** how many are on its list; null when the page only holds the first 1,000 of them */
  recipients: number | null;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    setBusy(true); setError('');
    try { await onConfirm(); } catch (e) { setError((e as Error).message); setBusy(false); }
  };
  return (
    <Modal
      narrow
      title={`Delete “${campaign.name}”?`}
      onClose={onClose}
      footer={(
        <>
          <button className="fs-btn danger solid" disabled={busy} onClick={() => void go()}>
            {busy ? 'Deleting…' : 'Delete campaign'}
          </button>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
        </>
      )}
    >
      {error && <div className="ad-err">{error}</div>}
      <p className="pm-confirm">
        {recipients === null
          ? 'The campaign, its copy and its list of recipients are deleted.'
          : `The campaign, its copy and its list of ${recipients.toLocaleString()} recipient${recipients === 1 ? '' : 's'} are deleted.`}
        {' '}There is no undo — sending it again means uploading the list again.
      </p>
    </Modal>
  );
}

export default function PlatformMarketing() {
  const { role } = usePlatform();
  const toast = useToast();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<CampaignListRow[]>([]);
  const [templates, setTemplates] = useState<MarketingTemplate[]>([]);
  const [window_, setWindow] = useState<SendWindow | null>(null);
  const [unsubscribed, setUnsubscribed] = useState(0);
  const [suppressed, setSuppressed] = useState(0);
  // The addresses behind that count (review finding 119) — read only when there are some.
  const [bounced, setBounced] = useState<{ rows: Suppression[]; days: number } | null>(null);
  const [smtpOk, setSmtpOk] = useState(true);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<CampaignListRow | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const readOnly = role !== 'superadmin';

  const load = () => listCampaignsApi()
    .then((r) => {
      setCampaigns(r.campaigns); setTemplates(r.templates); setWindow(r.window);
      setUnsubscribed(r.unsubscribed); setSuppressed(r.suppressed ?? 0); setSmtpOk(r.smtpConfigured); setDenied(false);
      if ((r.suppressed ?? 0) > 0) {
        void listSuppressionsApi()
          .then((s) => setBounced(Array.isArray(s.suppressions) ? { rows: s.suppressions, days: s.days } : null))
          .catch(() => setBounced(null));
      } else setBounced(null);
    })
    .catch((e) => { if (readOnly && refused(e)) setDenied(true); else setError((e as Error).message); });
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setError('');
    try { await fn(); toast(done); await load(); } catch (e) { setError((e as Error).message); }
  };

  const templateLabel = (key: string) => templates.find((t) => t.key === key)?.label || key;
  const best = bestOpenRate(campaigns);

  return (
    <div className="pl-page pm-page">
      <div className="fs-page-head pl-head">
        <div className="text">
          <h1>Marketing</h1>
          <div className="fs-page-sub">
            Email campaigns from the platform’s own address — they share the send pace with form invitations. {unsubscribed > 0 && `${unsubscribed} address${unsubscribed === 1 ? ' has' : 'es have'} unsubscribed and will always be skipped.`}
          </div>
        </div>
        {!readOnly && (
          <button className="fs-btn primary" onClick={() => setCreating(true)}><Plus>New campaign</Plus></button>
        )}
      </div>

      {denied ? (
        <div className="pl-note pm-denied">{NEEDS_SUPERADMIN} Campaigns, their lists and the send pace are kept to Super Admins.</div>
      ) : (
        <>
          {window_ && (
            <div className="pl-tiles pm-tiles">
              <FigureTile
                label="Last 24 hours" value={window_.lastDay.toLocaleString()}
                caption={`of ${window_.dailyCap.toLocaleString()}`} tone={window_.lastDay >= window_.dailyCap ? 'warn' : undefined}
              />
              <FigureTile
                label="This hour" value={window_.lastHour.toLocaleString()}
                caption={`of ${window_.hourlyCap.toLocaleString()}`} tone={window_.lastHour >= window_.hourlyCap ? 'warn' : undefined}
              />
              <FigureTile
                label="Best open rate" value={best ? wholePct(best.opened, best.delivered) : '—'}
                caption={best ? best.campaign.name : 'no opens yet'} tone={best ? 'ok' : undefined}
              />
              <FigureTile
                label="Unsubscribed" value={unsubscribed.toLocaleString()}
                caption={suppressed > 0
                  ? `always skipped · ${suppressed.toLocaleString()} bounced address${suppressed === 1 ? '' : 'es'} skipped`
                  : 'always skipped'}
              />
            </div>
          )}
          {window_ && <SendWindowCard window={window_} kind="Campaigns" />}
          {!smtpOk && (
            <div className="pl-warn">
              Outgoing email is not configured, so nothing can send — set it up under <EmailSettingsLink /> first.
            </div>
          )}
          {error && <div className="ad-err">{error}</div>}
          <TableCard
            className="pm-campaigns" title="Campaigns"
            footer={campaigns.length ? 'Opens are estimates: some mail apps load images on their own, and some never do.' : undefined}
          >
            {!campaigns.length ? (
              <div className="st-card-body st-muted st-empty">
                No campaigns yet. Start one from a template, upload a CSV of prospects, and prove the copy with a test send before anything goes out.
              </div>
            ) : (
              <table className="fs-table stack">
                <thead>
                  <tr>
                    <th>Campaign</th><th>Template</th><th>Status</th>
                    <th className="num">Recipients</th><th className="num">Sent</th>
                    <th className="num">Opened</th><th className="num">Bounced</th><th className="num">Failed</th>
                    <th>Created</th><th className="fs-row-actions" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id}>
                      <td data-label="Campaign"><Link className="pm-name" to={`/platform/marketing/${c.id}`}>{c.name}</Link></td>
                      <td data-label="Template" className="pm-muted">{templateLabel(c.template_key)}</td>
                      <td data-label="Status"><StatusPill status={c.status} /></td>
                      <td data-label="Recipients" className="num">
                        {c.total}{c.pending > 0 && <div className="fs-cell-sub faint">{c.pending} queued</div>}
                      </td>
                      <td data-label="Sent" className="num">{c.sent}</td>
                      <td data-label="Opened" className="num pm-rate">{openedCell(c)}</td>
                      <td data-label="Bounced" className="num pm-rate"><BouncedCell c={c} /></td>
                      <td data-label="Failed" className="num">{c.failed}</td>
                      <td data-label="Created" className="pm-date">{when(c.created_at)}</td>
                      <td className="fs-row-actions">
                        <RowMenu open={openMenuId === c.id} onToggle={(o) => setOpenMenuId(o ? c.id : null)} label={c.name}>
                          <button onClick={() => { setOpenMenuId(null); navigate(`/platform/marketing/${c.id}`); }}>Open</button>
                          {!readOnly && (c.status === 'draft' || c.status === 'paused') && (
                            <button onClick={() => { setOpenMenuId(null); void act(() => startCampaignApi(c.id), 'Campaign started — it drains at the platform pace.'); }}>Start sending</button>
                          )}
                          {!readOnly && c.status === 'sending' && (
                            <button onClick={() => { setOpenMenuId(null); void act(() => pauseCampaignApi(c.id), 'Campaign paused.'); }}>Pause</button>
                          )}
                          {!readOnly && c.status !== 'sending' && (
                            <>
                              <div className="rule" />
                              <button className="danger" onClick={() => { setOpenMenuId(null); setError(''); setDeleting(c); }}>Delete…</button>
                            </>
                          )}
                        </RowMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TableCard>
          {bounced && bounced.rows.length > 0 && (
            <TableCard
              className="pm-bounced" title="Bounced addresses"
              footer={`An address that hard-bounces is skipped by every campaign for ${bounced.days} days, then tried once more.`}
            >
              <table className="fs-table stack">
                <thead>
                  <tr><th>Address</th><th>What the mail server said</th><th>Bounced</th><th>Skipped until</th><th className="fs-row-actions" aria-label="Actions" /></tr>
                </thead>
                <tbody>
                  {bounced.rows.map((s) => (
                    <tr key={s.email}>
                      <td data-label="Address">{s.email}</td>
                      <td data-label="What the mail server said" className="faint">{s.detail || '—'}</td>
                      <td data-label="Bounced">{new Date(s.created_at).toLocaleDateString()}</td>
                      <td data-label="Skipped until">{new Date(s.expires_at).toLocaleDateString()}</td>
                      <td className="fs-row-actions">
                        {!readOnly && (
                          <button
                            className="fs-btn neutral sm"
                            onClick={() => void act(() => removeSuppressionApi(s.email), `${s.email} will be tried again.`)}
                          >
                            Try again
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          )}
        </>
      )}
      {creating && (
        <NewCampaignModal
          templates={templates}
          onClose={() => setCreating(false)}
          onCreated={(c) => { setCreating(false); navigate(`/platform/marketing/${c.id}`); }}
        />
      )}
      {deleting && (
        <DeleteCampaignModal
          campaign={deleting}
          recipients={deleting.total}
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await deleteCampaignApi(deleting.id);
            setDeleting(null);
            toast('Campaign deleted.');
            await load();
          }}
        />
      )}
    </div>
  );
}

function NewCampaignModal({ templates, onClose, onCreated }: {
  templates: MarketingTemplate[];
  onClose: () => void;
  onCreated: (c: MarketingCampaign) => void;
}) {
  const [name, setName] = useState('');
  const [templateKey, setTemplateKey] = useState(templates[0]?.key || 'generic');
  const [subject, setSubject] = useState(templates[0]?.subject || '');
  const [body, setBody] = useState(templates[0]?.body || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const template = templates.find((t) => t.key === templateKey);

  // Picking a template re-seeds the copy — that is what a template is for.
  const pick = (key: string) => {
    setTemplateKey(key);
    const t = templates.find((x) => x.key === key);
    if (t) { setSubject(t.subject); setBody(t.body); }
  };

  const create = async () => {
    setBusy(true); setError('');
    try {
      const r = await createCampaignApi({ name: name.trim() || (template?.label ?? 'Campaign'), templateKey, subject, body });
      onCreated(r.campaign);
    } catch (e) { setError((e as Error).message); setBusy(false); }
  };

  return (
    <Modal title="New campaign" wide onClose={onClose} footer={(
      <>
        <button className="fs-btn primary" disabled={busy || !subject.trim() || !body.trim()} onClick={() => void create()}>
          {busy ? 'Creating…' : 'Create draft'}
        </button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>
    )}>
      <div className="pm-form">
        {error && <div className="ad-err">{error}</div>}
        <div className="fs-row-2">
          <div className="fs-field">
            <label>Campaign name</label>
            <input aria-label="Campaign name" value={name} placeholder={template?.label || 'e.g. October carriers push'} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="fs-field">
            <label>Template</label>
            <select aria-label="Template" value={templateKey} onChange={(e) => pick(e.target.value)}>
              {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
        </div>
        {template && <p className="pm-note">Written at: {template.audience}. Edit anything below — the template is a starting point, not the send.</p>}
        <div className="fs-field">
          <label>Subject</label>
          <input aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="fs-field">
          <label>Body</label>
          <textarea aria-label="Body" rows={12} value={body} onChange={(e) => setBody(e.target.value)} />
          <span className="hint">{'{{name}}'} becomes each recipient’s name (or “there”). The pricing button and the unsubscribe footer are added to every send automatically.</span>
        </div>
      </div>
    </Modal>
  );
}

/**
 * A recipient's place in the queue, as the outbox left it — and, once sent,
 * what came of it. A hard bounce outranks an open (the email never reached an
 * inbox); an open outranks a temporary refusal, which the retry that follows
 * one can still get through.
 */
function RecipientPill({ r }: { r: MarketingRecipient }) {
  if (r.message_id) {
    const bounce = r.bounced_at
      ? <span className="fs-pill danger" title={r.bounce_detail || ''}>{r.bounce_kind === 'soft' ? 'bounced (temporary)' : 'bounced'}</span>
      : null;
    if (bounce && r.bounce_kind !== 'soft') return bounce;
    if (r.first_opened_at) {
      const times = r.open_count ?? 1;
      return (
        <span className="fs-pill ok" title={`Opened ${times} time${times === 1 ? '' : 's'}, first on ${when(r.first_opened_at)}`}>
          opened
        </span>
      );
    }
    return bounce || <span className="fs-pill ok">sent</span>;
  }
  if (r.failed_at) {
    return (
      <span className="fs-pill danger" title={r.error || ''}>
        {r.error === 'On the unsubscribe list' ? 'unsubscribed' : 'failed'}
      </span>
    );
  }
  if (r.sent_at) return <span className="fs-pill caution">sending</span>;
  return <span className="fs-pill neutral">queued</span>;
}

/** The most recipients the campaign read carries (the server's listRecipients cap). */
const RECIPIENTS_READ = 1000;

/**
 * Sent · Opened · Bounced for one campaign, counted from the recipients the
 * page holds; nothing until something has been sent. A list longer than the
 * read is counted over its first thousand, and says so.
 */
function CampaignTiles({ recipients }: { recipients: MarketingRecipient[] }) {
  const sent = recipients.filter((r) => r.message_id).length;
  if (!sent) return null;
  const hard = recipients.filter((r) => r.message_id && r.bounced_at && r.bounce_kind !== 'soft').length;
  const soft = recipients.filter((r) => r.message_id && r.bounced_at && r.bounce_kind === 'soft').length;
  const opened = recipients.filter((r) => r.message_id && r.first_opened_at).length;
  const delivered = deliveredOf(sent, hard);
  const capped = recipients.length >= RECIPIENTS_READ;
  return (
    <>
      <div className="pl-tiles pm-tiles">
        <FigureTile
          label="Sent" value={sent.toLocaleString()}
          caption={`of ${capped ? 'the first ' : ''}${recipients.length.toLocaleString()}`}
        />
        <FigureTile
          label="Opened" value={opened.toLocaleString()}
          caption={delivered > 0 ? `${wholePct(opened, delivered)} of delivered` : 'nothing delivered'}
        />
        <FigureTile
          label="Bounced" value={hard.toLocaleString()}
          caption={`${tenthPct(hard, sent)} of sent${soft ? ` · ${soft.toLocaleString()} temporary` : ''}`}
          tone={bounceTone(hard, sent)}
        />
      </div>
      <p className="pm-note">
        {capped && `Counted over the first ${RECIPIENTS_READ.toLocaleString()} recipients on the list — this page reads no more of it. `}
        Opens are estimates: some mail apps load images on their own, and some never do.
      </p>
    </>
  );
}

export function MarketingCampaignPage() {
  const { id } = useParams();
  const campaignId = Number(id);
  const { role } = usePlatform();
  const toast = useToast();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<MarketingCampaign | null>(null);
  const [recipients, setRecipients] = useState<MarketingRecipient[]>([]);
  const [window_, setWindow] = useState<SendWindow | null>(null);
  const [smtpOk, setSmtpOk] = useState(true);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const readOnly = role !== 'superadmin';

  const load = () => getCampaignApi(campaignId)
    .then((r) => {
      setCampaign(r.campaign); setRecipients(r.recipients); setWindow(r.window); setSmtpOk(r.smtpConfigured);
      setSubject(r.campaign.subject); setBody(r.campaign.body);
    })
    .catch((e) => { if (readOnly && refused(e)) setDenied(true); else setError((e as Error).message); });
  useEffect(() => { void load(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true); setError('');
    try { await fn(); toast(done); await load(); } catch (e) { setError((e as Error).message); }
    setBusy(false);
  };

  const onCsv = async (file: File) => {
    setBusy(true); setError('');
    try {
      const r = await uploadCampaignCsvApi(campaignId, file);
      toast(`${r.added} added${r.alreadyOnList ? `, ${r.alreadyOnList} already on the list` : ''}${r.invalid ? `, ${r.invalid} unusable row${r.invalid === 1 ? '' : 's'}` : ''}.`);
      await load();
    } catch (e) { setError((e as Error).message); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const back = <Link className="pl-back" to="/platform/marketing">← Marketing</Link>;
  const frame = (children: ReactNode) => <div className="pl-page pm-campaign">{back}{children}</div>;
  if (denied) return frame(<div className="pl-note pm-denied">{NEEDS_SUPERADMIN}</div>);
  if (error && !campaign) return frame(<div className="ad-err">{error}</div>);
  if (!campaign) return frame(<div className="st-muted">Loading…</div>);
  const copyLocked = campaign.status !== 'draft';
  const copyDirty = subject !== campaign.subject || body !== campaign.body;

  return frame(
    <>
      <div className="fs-page-head pl-head">
        <div className="text">
          <div className="pm-titlerow"><h1>{campaign.name}</h1><StatusPill status={campaign.status} /></div>
          <div className="fs-page-sub">Prove the copy in your own inbox, upload the list, then start it.</div>
        </div>
        {!readOnly && (
          <div className="pl-head-actions">
            {(campaign.status === 'draft' || campaign.status === 'paused') && (
              <button className="fs-btn primary" disabled={busy || !smtpOk || !recipients.length}
                onClick={() => void act(() => startCampaignApi(campaignId), 'Campaign started — it drains at the platform pace.')}>
                Start sending
              </button>
            )}
            {campaign.status === 'sending' && (
              <button className="fs-btn primary" disabled={busy}
                onClick={() => void act(() => pauseCampaignApi(campaignId), 'Campaign paused.')}>
                Pause
              </button>
            )}
            <button className="fs-btn neutral" disabled={busy || !smtpOk}
              onClick={() => void act(() => testCampaignApi(campaignId), 'Test sent to your own address.')}>
              Send me a test
            </button>
            <button className="fs-btn neutral" disabled={busy}
              onClick={() => void previewCampaignApi(campaignId).then((r) => setPreview({ subject: r.subject, html: r.html })).catch((e) => setError((e as Error).message))}>
              Preview
            </button>
          </div>
        )}
      </div>
      <CampaignTiles recipients={recipients} />
      {window_ && <SendWindowCard window={window_} kind="Campaigns" />}
      {!smtpOk && <div className="pl-warn">Outgoing email is not configured — set it up under <EmailSettingsLink /> first.</div>}
      {error && <div className="ad-err">{error}</div>}

      <section className="fs-card pm-copy" aria-labelledby="pm-copy-title">
        <div className="st-card-head"><h2 id="pm-copy-title">The email</h2></div>
        <div className="st-card-body pm-form">
          {copyLocked && <p className="pm-note">The copy locked when sending started, so everyone on the list gets the same words.</p>}
          <div className="fs-field">
            <label>Subject</label>
            <input aria-label="Subject" value={subject} disabled={readOnly || copyLocked} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="fs-field">
            <label>Body</label>
            <textarea aria-label="Body" rows={10} value={body} disabled={readOnly || copyLocked} onChange={(e) => setBody(e.target.value)} />
            <span className="hint">{'{{name}}'} becomes each recipient’s name. The pricing button and unsubscribe footer are always added.</span>
          </div>
          {!readOnly && !copyLocked && (
            <div className="st-actions">
              <button className="fs-btn primary" disabled={busy || !copyDirty || !subject.trim() || !body.trim()}
                onClick={() => void act(() => updateCampaignApi(campaignId, { subject, body }), 'Copy saved.')}>
                Save copy
              </button>
            </div>
          )}
        </div>
      </section>

      <TableCard
        className="pm-recipients"
        title={`Recipients (${recipients.length})`}
        hint="A CSV with an email column, and optionally a name (or first/last name) column. Addresses already on the unsubscribe list are skipped automatically."
        action={!readOnly ? (
          <label className="fs-btn neutral pm-upload">
            Upload CSV…
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onCsv(f); }} />
          </label>
        ) : undefined}
      >
        {!recipients.length ? (
          <div className="st-card-body st-muted st-empty">Nobody yet — upload a CSV.</div>
        ) : (
          <table className="fs-table stack">
            <thead><tr><th>Email</th><th>Name</th><th>Status</th><th className="fs-row-actions" aria-label="Actions" /></tr></thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.id}>
                  <td data-label="Email" className="pm-email">{r.email}</td>
                  <td data-label="Name">{r.name || <span className="pm-muted">—</span>}</td>
                  <td data-label="Status"><RecipientPill r={r} /></td>
                  <td className="fs-row-actions">
                    {!readOnly && !r.sent_at && !r.failed_at && (
                      <button className="fs-btn icon" title="Remove from list" aria-label={`Remove ${r.email}`}
                        onClick={() => void act(() => removeCampaignRecipientApi(campaignId, r.id), 'Removed.')}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>

      {!readOnly && campaign.status !== 'sending' && (
        <div className="pm-danger">
          <button className="fs-btn danger" disabled={busy} onClick={() => { setError(''); setConfirmDelete(true); }}>
            Delete campaign…
          </button>
        </div>
      )}

      {confirmDelete && (
        <DeleteCampaignModal
          campaign={campaign}
          // the page reads at most the first 1,000 of a list (listRecipients' cap)
          recipients={recipients.length < 1000 ? recipients.length : null}
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            // Away only once the server has said yes — a refusal (a campaign
            // that started sending meanwhile) keeps you here with its sentence.
            await deleteCampaignApi(campaignId);
            toast('Campaign deleted.');
            navigate('/platform/marketing');
          }}
        />
      )}

      {preview && (
        <Modal title={`Preview — ${preview.subject}`} wide onClose={() => setPreview(null)}>
          {/* Our own renderer's output for a sample recipient — trusted HTML from our server, not user input. */}
          <div className="pm-preview" dangerouslySetInnerHTML={{ __html: preview.html }} />
        </Modal>
      )}
    </>
  );
}
